/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { toast } from 'utils/toastUtils'
import { useAppDispatch, AppDispatch } from 'helpers/store'
import { closeModal } from 'helpers/actions'
import { RemindersAction } from 'store/reminders/RemindersAction'
import SessionAction from 'store/session/SessionAction'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import ReminderForm from 'components/Reminders/forms/ReminderForm'
import { Reminder } from 'types/entities/Reminder'
import { getReminderModelConfig } from 'utils/reminders/reminderUtils'
import { openEntityByModelName } from 'utils/reminders/openEntityByModelName'
import clsx from 'clsx'

interface EditReminderModalProps {
    reminder: Reminder
    onSuccess?: () => void
}

const EditReminderModal: React.FC<EditReminderModalProps> = ({ reminder, onSuccess }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const modelConfig = getReminderModelConfig(reminder.model_name)
    const canOpenEntity = reminder.model_id != null && Boolean(modelConfig)

    const handleSubmit = async (data: any, shouldClose: boolean = false) => {
        try {
            const payload = { ...data, id: reminder.id }
            await dispatch(RemindersAction.fetchCreateReminder(payload))
            dispatch(SessionAction.fetchNotifications() as any)
            toast.success(t('Reminder updated successfully'))
            onSuccess?.()
            if (shouldClose) {
                dispatch(closeModal())
            }
        } catch (e) {
            toast.error(t('Failed to update reminder'))
            throw e
        }
    }

    const handleOpenEntity = async () => {
        dispatch(closeModal())
        await openEntityByModelName({
            dispatch: dispatch as unknown as AppDispatch,
            t,
            navigate,
            modelName: reminder.model_name,
            modelId: reminder.model_id,
        })
    }

    return (
        <UiFormModalShell
            className="EditReminderModal"
            title={t('Edit Reminder')}
            subtitle={`${t('Editing reminder')} #${reminder.id}`}
        >
            <div className="max-w-4xl mx-auto flex flex-col gap-4">
                {canOpenEntity && (
                    <button
                        type="button"
                        onClick={handleOpenEntity}
                        className={clsx(
                            'self-start inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                            modelConfig
                                ? [modelConfig.colorClass, modelConfig.textColorClass]
                                : 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
                        )}
                    >
                        <span>
                            {t('Attached to')} {t(modelConfig?.label ?? reminder.model_name)} #
                            {reminder.model_id}
                        </span>
                        <ArrowTopRightOnSquareIcon className="size-3.5 shrink-0 opacity-70" />
                    </button>
                )}
                <ReminderForm reminder={reminder} onSubmit={handleSubmit} />
            </div>
        </UiFormModalShell>
    )
}

export default EditReminderModal
